import { isValidEmail, normalizeEmail } from '@/core/utils/email'
import { Switch } from '@components/buttons/switch'
import { CustomTextField } from '@atbay/component-lib'
import { Controller, useFormContext } from 'react-hook-form'
import { EmailAlertsWrapper, StyledForm } from './inviteUserForm.styles'
import Typography from '@/ui/components/typography'

export interface InviteUserFormProps {
    submitted: boolean
}

const InviteUserForm = ({ submitted }: InviteUserFormProps) => {
    const {
        formState: { errors, isDirty },
        control,
        trigger,
        getValues,
        setValue,
    } = useFormContext()

    const onChange = () => {
        submitted && isDirty && trigger()
    }

    const onBlur = () => {
        setValue('email', normalizeEmail(getValues().email))
    }

    return (
        <StyledForm onChange={onChange} onBlur={onBlur}>
            <Controller
                name="first_name"
                control={control}
                rules={{
                    required: true,
                    minLength: {
                        value: 2,
                        message: 'First name is limited to 2 characters',
                    },
                }}
                render={({ field }) => (
                    <div>
                        <CustomTextField
                            {...field}
                            inputType="text"
                            label="Enter first name"
                            textValue={field.value}
                            error={!!errors?.first_name}
                            accessibilityId="first_name"
                        />
                        <p className="error">
                            {(errors?.first_name?.message as string) || ''}
                        </p>
                    </div>
                )}
            />
            <Controller
                name="last_name"
                control={control}
                rules={{
                    required: true,
                    minLength: {
                        value: 2,
                        message: 'Last name is limited to 2 characters',
                    },
                }}
                render={({ field }) => (
                    <div>
                        <CustomTextField
                            {...field}
                            inputType="text"
                            label="Enter last name"
                            textValue={field.value}
                            error={!!errors?.last_name}
                            accessibilityId="last_name"
                        />
                        <p className="error">
                            {(errors?.last_name?.message as string) || ''}
                        </p>
                    </div>
                )}
            />

            <Controller
                name="job_title"
                control={control}
                rules={{
                    required: true,
                    maxLength: {
                        value: 100,
                        message: 'Job title is limited to 100 characters',
                    },
                }}
                render={({ field }) => (
                    <div>
                        <CustomTextField
                            {...field}
                            inputType="text"
                            label="Enter job title"
                            textValue={field.value}
                            error={!!errors?.job_title}
                            accessibilityId="job_title"
                        />
                        <p className="error">
                            {(errors?.job_title?.message as string) || ''}
                        </p>
                    </div>
                )}
            />

            <Controller
                name="email"
                control={control}
                rules={{
                    required: true,
                    validate: (value) =>
                        isValidEmail(value) || 'Invalid email address',
                }}
                render={({ field }) => (
                    <div>
                        <CustomTextField
                            {...field}
                            inputType="email"
                            label="Enter email address"
                            textValue={field.value}
                            error={!!errors?.email}
                            accessibilityId="email"
                        />
                        <p className="error">
                            {(errors?.email?.message as string) || ''}
                        </p>
                    </div>
                )}
            />

            <Controller
                name="email_alerts"
                control={control}
                defaultValue={true}
                rules={{ required: false }}
                render={({ field: { value, onChange } }) => {
                    return (
                        <EmailAlertsWrapper>
                            <Typography variant="s-regular">
                                Send email alerts
                            </Typography>
                            <Switch checked={value} onChange={onChange} />
                        </EmailAlertsWrapper>
                    )
                }}
            />
            <p className="error">
                {(errors?.email_alerts?.message as string) || ''}
            </p>
        </StyledForm>
    )
}

export default InviteUserForm
